const BASE_SYMBOL_SIZE = 155;
const BASE_NODE_GAP = 800;
const BASE_LEVEL_HEIGHT = 1200;

// Exported so auto-spacing derives its bounds from the same baselines that
// applyCanvasPreferences uses (no drift if a baseline ever changes).
export { BASE_SYMBOL_SIZE, BASE_NODE_GAP, BASE_LEVEL_HEIGHT };

export const config = {
  SYMBOL_SIZE: BASE_SYMBOL_SIZE,

  MIN_SYMBOL_GAP: 10,
  NODE_GAP: BASE_NODE_GAP,
  MIN_GAP: 800,
  GRID_SIZE: 100,

  VIRTUAL_X_REQUIRED_BETWEEN_PEOPLE: 1,
  VIRTUAL_X_REQUIRED_BETWEEN_PARENTS: 1,

  LEVEL_HEIGHT: BASE_LEVEL_HEIGHT,

  SPOUSE_INCLINE: 20,

  PEDIGREE_OFFSET: 0,

  MAX_ITERATIONS_FOR_SPACING: 50,
  MAX_ITERATIONS_FOR_FIX_PARENTS: 50,
  ENABLE_FIX_POSITION_OF_PARENTS: true,

  ENABLE_SQUEEZE_GENERATIONS: false,
  ENABLE_REMOVE_EMPTY_RANGES: false,
  ENABLE_BRING_PARENTS_CLOSER: true,
  ENABLE_ALIGN_GENERATIONS_CENTRE: false,
  ENABLE_AUTO_SORTING_OF_SUBTREES: true,

  MAX_ITERATIONS_FOR_SQUEEZE: 10,
  MAX_ITERATIONS_FOR_BRING_PARENTS_CLOSER: 20,
  MAX_ITERATIONS_FOR_REMOVE_GAPS: 5,

  MAX_TIME_FOR_SQUEEZE: 5000,
  MAX_TIME_FOR_BRING_PARENTS_CLOSER: 3000,
  MAX_TIME_FOR_REMOVE_GAPS: 2000,

  // When true, pedigree() uses the deterministic single-pass layoutV2 (post-order
  // tidy-tree with first-class union nodes) instead of the V1 relaxation solver.
  // See claude-MDs/PLAN-pedigree-layout-simplification.md.
  ENABLE_LAYOUT_V2: true,

  // When true, layoutV2 uses a dedicated layer-ordering stage (sibship-contiguous,
  // cross-married members floated to adjacent sibship edges, married-in spouses kept
  // beside their partner) feeding the proven order-preserving centring/de-overlap
  // passes, plus a leftward gap-compaction — instead of the graph-centre spanning-tree
  // placement and the cross-marriage corrective sweeps. Fixes sibling/spouse ambiguity
  // and erratic spacing. See claude-MDs/PLAN-pedigree-layout-ordering-and-spacing.md.
  // The default layout engine (Jun 2026) — on permanently.
  ENABLE_LAYOUT_V3: true,

  // V3 only: iterations of the PeopleGroup up/down reorder loop (ported from the
  // Vertigo desktop's Config.MaxIterationsForReOrderingGroupsOfPeople). The loop
  // also early-exits on convergence; this is the failsafe cap.
  MAX_ITERATIONS_FOR_REORDERING_GROUPS: 10,

  // V3 only: iterations of the outer relaxation loop (order ⇄ position). Each
  // iteration re-derives the within-generation order from the positions the
  // previous iteration produced, so married-in parents end up ordered for where
  // their children ACTUALLY landed (not the initial seed) — eliminating the
  // dog-legs/double hop-overs that a single pass leaves. Early-exits when the
  // per-generation order stops changing; this is the failsafe cap.
  MAX_ITERATIONS_FOR_V3_RELAYOUT: 8,

  // When true, siblings are ordered by BIRTH ORDER (oldest→youngest, left→right):
  // DOB → age → PID fallback, scoped per sibship, twins kept as an adjacent block.
  // When false, siblings keep legacy PID/entry order. See
  // claude-MDs/PLAN-sibling-birth-order.md. Used by both layout engines.
  ENABLE_BIRTH_ORDER_SORT: true,

  // When a sibship is dragged far from its own parents' union because one sibling
  // cross-married a consanguineous (in-tree) cousin on the opposite founder branch, its
  // sibling-line connector becomes a long horizontal bar that the layout cannot shorten
  // (same-row pure-x model; union.x is pinned to the parent midpoint). This keeps the
  // sibship IN LINE with its generation and routes only the sibling-line OVER THE TOP —
  // raised to a lane above the parents' row, parallel to the generation line — so it
  // clears the unrelated co-generation drops it would otherwise sweep (C1373: 12 → 3
  // crossings). Strictly gated (see displacedSibshipLift.js): it does not fire on a
  // balanced cross-marriage, and a rescued (runaway-guarded) layout is skipped. Across
  // the corpus it fires on C1373 alone (the isolation leans on the runaway-guard skip,
  // not geometry alone — the malformed C1437 shares the geometric signature).
  ENABLE_DISPLACED_SIBSHIP_LIFT: true,
  // Over-the-top lane height as a fraction of LEVEL_HEIGHT ABOVE the parents' (union)
  // row. >=0.75 clears the gen-1 drops and floors at the 3 inherent founder-drop
  // crossings; below that the lane is too low and re-crosses (0.5 → 7). See PLAN R27.
  DISPLACED_SIBSHIP_OVERTOP_LANE: 0.75,

  // Final safety net: a bare zero-degree pendant spouse (no parents, no children, a
  // single marriage) that some pass stranded far from its partner leaves a
  // chart-spanning marriage line through EMPTY space — which crosses/overlaps nothing,
  // so it evades the crossing + marriage-overlap gates entirely (G-1-01388). After the
  // pipeline settles, any such pendant whose marriage exceeds MAX_MARRIAGE_SPAN is
  // pulled adjacent to its partner via the overlap-safe rigid move. Repair is restricted
  // to a bare pendant, so it can NEVER fight a legitimate wide marriage (e.g. a hub
  // flank, which is not a bare pendant). See displacedSibshipLift.js / canPersonBeMovedV3
  // (the structural fixes that prevent the known cause); this is defence-in-depth.
  ENABLE_RUNAWAY_MARRIAGE_GUARD: true,
  // Bare-pendant marriage span (in NODE_GAP units) above which the guard repairs. Well
  // above any legitimate adjacent pendant (~1); legitimate WIDE marriages (hub flanks up
  // to ~8.6) are never touched because they are not bare pendants.
  MAX_MARRIAGE_SPAN: 6,

  // Class-B stretched-bridge decouple (detectStretchBridgesV3). When two ancestry
  // components are joined by a SINGLE childless/proband-leaf cross-marriage whose
  // converged span exceeds this, the smaller component is decoupled and relocated
  // adjacent to its partner (a short marriage line). Set ABOVE MAX_MARRIAGE_SPAN
  // because relocating a whole block is a heavier action than the bare-pendant repair:
  // it must only fire on a genuinely chart-spanning bridge. The decouple targets stretch
  // to 8.9u/9.5u/76u; the nearest golden single-bridge candidate (the decision-pending
  // C1389 1:4) settles at 7.5u — so this sits in the (7.5, 8.9) gap to keep every golden
  // byte-identical. (Hub flanks reach ~8.6u but are excluded structurally by the
  // single-marriage gate, not by this threshold.)
  STRETCH_BRIDGE_DECOUPLE_SPAN: 8.2,

  // Global ancestry-width reservation (V3 only) — PROTOTYPE, gated OFF. The corrective
  // centring (fixPositionOfParentsV3) cannot pull a parent couple onto its children
  // when the couple is wedged in a packed row with no room to shift — the rigid-drag
  // move aborts, leaving the union far off its child (the G-1-01391 "two-towered
  // proband" class: union-30-31 off ~4.9u). When ON, each node reserves horizontal
  // room for the FULL width of its ancestry tower (towerHalfV3) BOTH between adjacent
  // units (boundaryClearanceV3) AND, via the couple half-offset in fixPositionOf
  // ParentsV3, between the two partners of an ancestry-bearing marriage — so every
  // parent couple has space to centre over its descendants.
  //
  // MEASURED (PLAN Round 28): geometrically sound — ZERO node overlaps introduced
  // across the whole corpus, and it substantially centres the target (01391 union-30-31
  // 4.9u → 1.9u, crossings 5 → 1; corpus crossings net-improve). BUT it is a genuine
  // tradeoff, not a clean win: charts sprawl (01391 ~24u → ~70u wide), marriage lines
  // lengthen broadly (01391 proband 9.4u → 13.5u; C1390 25u → 39u), an ancestry-bearing
  // couple's spread can tear a childless sibling off its sibship (a long sibling bar),
  // and it merely RELOCATES the off-centre problem to other couples (01391 union-16-17
  // 2.0u → 4.1u). Capping the reservation is non-monotonic (no value cleanly wins). A
  // truly clean fix needs a contour-merging tidy-tree x-assignment (the rejected big
  // rewrite). Left OFF (byte-identical) as a characterised prototype; flipping it on
  // ALSO re-baselines every hand-drawn golden. See
  // claude-MDs/PLAN-pedigree-layout-ordering-and-spacing.md (Round 28).
  ENABLE_TOWER_WIDTH_RESERVATION: false,

  // Contour-merge x-assignment (V3 only) — PROTOTYPE, gated OFF. The principled fix for
  // off-centre ancestor unions (G-1-01391 union-30-31) WITHOUT the tower-width
  // reservation's sprawl. A Reingold–Tilford tidy-tree over the proband-rooted spanning
  // tree (reusing V1's buildComponentTrees/classifyNeighbours + direction-aware centring:
  // union over children, ancestry over pivot), but adjacent subtrees are pushed together
  // by their actual PER-GENERATION contour (nesting where the towers recede) instead of by
  // a reserved sum — so the chart grows as the envelope, not the sum. Keyed on the FIXED
  // generation rows (virtualY). Validated in isolation (contourMerge.js / its tests +
  // contourMergeAncestry.test.js: 01391 ancestry nests to 18.5u). When ON it produces a
  // different x distribution → re-baselines every hand-drawn golden, so it stays OFF until
  // measured-good + signed off. See claude-MDs/PLAN-pedigree-contour-merge-xassignment.md.
  //
  // The apportionment is now a POST-ORDER CONTOUR over a descent forest (see
  // _contourApportion + claude-MDs/PLAN-pedigree-dogleg-elimination.md): on tree-like and
  // proband-reported families it roughly halves the dog-legs of the old relaxation with no
  // sprawl, but a descent-only tree can centre only one side of a balanced cross-marriage,
  // so a few fully-ancestried families gain parallel marriage lines. Kept OFF (gated
  // prototype) until that is resolved or explicitly accepted as the global default — which
  // also re-baselines every hand-drawn golden.
  ENABLE_CONTOUR_MERGE_XASSIGN: true,

  // UN-FLANKED 2-SPOUSE HUB LIFT (default off, gated prototype). A hub is normally FLANKED
  // (spouse│hub│spouse) so each side holds one spouse; but a hub pinned at a row EDGE (e.g. an
  // adopted-in node under its parents' descent) seats BOTH spouses on one side, and the 2nd's
  // marriage bar then lane-routes (dips below the row) over the 1st. When ON, an extra pass in
  // refineCrossingsAwareV3 angles that 2nd spouse upward (the same lift a 3rd/4th spouse gets)
  // BUT only when it does not raise the crossing count — a blind lift regressed the corpus (a
  // lifted diagonal can cross other edges; the lane-dip is sometimes fewer). Flag off ⇒ the
  // 2-spouse path is skipped in both buildRenderModel and the refine, so every chart is
  // byte-identical. See reference_extra_spouse_lift_gate.
  ENABLE_UNFLANKED_SPOUSE_LIFT: false,

  // HUB-REMARRIAGE STRAIGHTEN (TGCLOUD-1456). A multi-spouse hub's SECOND (remarriage)
  // union drops its child from the union midpoint, but the shared hub node is pinned by
  // its OTHER union, so the main crossing-refine hill-climb (which moves PARENTS) can only
  // oscillate to a compromise dog-leg (node 10 under hub 3, not under union 3-8). A final
  // guarded pass in crossingRefine instead moves the remarriage union's CHILDREN under the
  // union midpoint — KEPT only if it adds no crossing / no marriage-over-node and net-reduces
  // the dog-leg, so it is corpus-safe by construction (rejected where there's no room). Flag
  // OFF ⇒ the pass never runs (byte-identical). See claude-MDs/PLAN-pedigree-dogleg-1456.md.
  ENABLE_HUB_REMARRIAGE_STRAIGHTEN: true,

  // HUB TOWER ORDER (TGCLOUD-1468). When a multi-spouse hub's two spouses share a gender
  // opposite the hub, linkGroupsV3's gender-first parent-tower discovery wedges a spouse's
  // ancestry tower between the proband couple's towers, splitting the couple (marriage line
  // over a node) and interleaving the hub's other-union child. This makes a hub group
  // discover its parent towers in VISUAL order instead — restoring the flanked seed order
  // orderGenerationsV3 already produced. No-op for cross-married couples + same-gender
  // co-spouse hubs. See claude-MDs/PLAN-tgcloud-1468-cross-family-hub-ordering.md.
  ENABLE_HUB_TOWER_ORDER: true,

  // HUB EDGE-STRADDLE (TGCLOUD-1468c). A multi-spouse hub whose remarriage union also has
  // children sits at its primary sibship's OUTER edge (asymmetric straddle, union midpoint
  // preserved over the children) so the remarriage cluster + child re-separate CLEAR of the
  // sibship instead of wedged inside it (the interleave/dog-leg). The primary marriage line
  // stretches across its own sibship — the accepted trade (Kathryn, TGCLOUD-1456/1468).
  // Strictly gated (exactly-2-spouse hub, ≥2 children, flanked order, single-hub couple) and
  // a pure SPACING change (row order unchanged) so it composes with the established-order
  // lock. See claude-MDs/PLAN-tgcloud-1468-cross-family-hub-ordering.md.
  ENABLE_HUB_EDGE_STRADDLE: true,

  // PARTNER-TOWER SLIDE (TGCLOUD-1468 / G-1-01396). A both-ancestried couple whose two
  // grandparent towers pack tightly can't centre both towers over their parents — the
  // widening handles the symmetric case, but when the couple then gets re-separated into a
  // row-neighbour, one tower ends up off-centre (u(24,25)→22 dog-leg). This crossingRefine
  // sweep slides ONE partner + its PURE ancestor tower away from its spouse (the user's
  // manual fix: "move 23 + 26-27 right"), recentring the union over its children and
  // freeing the slack the blocked tower needs. Pareto-gated on the real detector + order-
  // preserving, so it can never regress and composes with the established-order lock.
  ENABLE_PARTNER_TOWER_SLIDE: true,

  // ASYM-JUNCTION PARK (G-1-01409 node 9). The straightening passes hold a hub-remarriage
  // union's MIDPOINT over its children, so when the hub is pushed aside the free spouse is
  // mirrored outward by the same amount — flung far from the family. Desktop convention
  // allows an ASYMMETRIC bar: crossingRefine's final park pass pulls the free single-married
  // spouse (with its pure tower) back to the standard partner offset from the child column
  // and the union junction anchors over the children's midpoint instead (only the hub-side
  // bar segment stretches). Pareto-gated + order-preserving.
  ENABLE_ASYM_JUNCTION_PARK: true,

  // COUPLE ORIENTATION NORMALISE (SS03 / G-1-01429-2, 2026-07-12 — Tim's rule 3:
  // male on the left unless flipping buys real layout quality). The seed places
  // couples male-left, but the orientation-blind block-swap search
  // (_reorderForContourV3) can adopt a side flip mid-climb and never restore it
  // when the settled score ties mirrored. A final refine pass (crossingRefine.js,
  // unlocked only) swaps a gratuitously female-left couple's columns — rigidly
  // translating each partner's pure ancestry tower — and adopts ONLY when the
  // flip is free (Pareto on cross/over/dog + overlap-safe). Skips hubs, bridges
  // and shared-tower pairs. Kill switch for this pass.
  ENABLE_COUPLE_ORIENTATION_NORMALISE: true,

  // BIDIRECTIONAL contour (ON). The shipped descent-forest contour cannot interleave an
  // ancestry tower with a descent tower, so a BOTH-ANCESTRIED couple (both partners have drawn
  // parents) is SPLIT — its marriage line spans the row over the intervening families, plus
  // the hub/sibling-fanning failures it causes (G-1-01395, G-1-01396). This contour places each
  // parent's ancestry ABOVE and descent BELOW (contour-merged so towers nest, V3's settled
  // order preserved), then runs a crossing-minimising BLOCK-SWAP search (what a human does:
  // move whole family blocks to put parents above children). It is NOT applied blindly: when a
  // family has a both-ancestried couple, layout.js lays it out BOTH ways (descent-forest AND
  // bidirectional) and keeps whichever has fewer hop-overs (crossings + marriage-over-node) —
  // BEST-OF-BOTH. So this can never regress a family below the shipped layout (every golden
  // holds); it is adopted only where it genuinely reduces hop-overs. OFF = the pure shipped
  // descent-forest. See claude-MDs/PLAN-pedigree-bidirectional-contour.md +
  // project_pedigree_crossing_reorder.
  ENABLE_BIDIRECTIONAL_CONTOUR: true,

  // ESTABLISHED ORDER (Phase 2 — "sit and forget"; default OFF until the persistence +
  // drag wiring + browser verification land). When a pedigree has a persisted
  // within-generation order, the engine SEEDS that order and runs the apportionment ONLY —
  // skipping best-of-both + the crossing-minimising reorder — so adding/removing a relative
  // never reshuffles the people already placed (Kathryn's "don't flip people around"). New
  // people slot in beside a ranked relative; an explicit Auto-arrange clears the order to
  // re-run the full optimiser. See claude-MDs/PLAN-pedigree-stability-and-ux.md (Phase 2).
  // NOTE (2026-06-29): turned ON. 2A (establish + lock + add-stability) is live; drag-to-
  // reorder (2B) and old-pedigree migration (2D) are NOT yet built — see the caveats in the
  // PLAN before deploying. Engine tests are unaffected (they pass no establishedOrder).
  ENABLE_ESTABLISHED_ORDER: true,

  // LOCKED SCOPED REFINE (Phase C2 — "refine only the node you just added"). When a
  // persisted order is in force, the crossing-minimising search is normally skipped
  // entirely (so established people never move). With this on, a RESTRICTED refine runs:
  // it may re-slot only the UNRANKED (newly-added) nodes among the fixed ranked ones —
  // never changing any established node's relative order — keeping the newcomer as clean as
  // possible without disturbing what the user already arranged. Inert unless a pedigree has
  // an establishedOrder AND at least one unranked node, so it never touches the corpus
  // (which carries no order). See claude-MDs/PLAN-pedigree-live-edit-stability.md (Phase C).
  ENABLE_LOCKED_SCOPED_REFINE: true,

  // ARRANGE BEACON (the Tim/Kathryn middle ground, 2026-07-11). While drawing, the layout
  // stays LOCKED to the established order (nothing the user placed ever moves on an add —
  // Kathryn's "track the changes as you draw"), and the engine compares each locked result
  // against the from-scratch optimum it already computes for the fixpoint fast-path. When
  // from-scratch is STRICTLY better (fewer crossings/marriage-overs, or ≥1 grid unit less
  // dog-leg — the D7 deadband, so a mere mirror-flip never counts), the model is flagged
  // `arrangeHint` and the UI lights the Auto-arrange button: the USER drives the re-layout
  // (Tim's "accept the better result", by explicit click). OFF restores as-drawn mode
  // (d9412899): live adds re-optimise freely and may reorder existing people. See
  // claude-MDs/PLAN-pedigree-drawing-journey-stability.md (R3a).
  ENABLE_ARRANGE_BEACON: true,

  // UNDO/REDO (Phase 1 of PLAN-pedigree-undo-redo-and-saved-views, v1.1). Session-only
  // history of the three layout states (customPositions / establishedOrder / orderLocked).
  // Checkpoints are recorded at the user-intent mutation sites only (drag-reorder, pin
  // drops, arrow nudges, Clear/Reset position, Auto-arrange) — NEVER on the engine's
  // settled-layout auto-capture, whose merges fold into the adjacent user step. Undo/Redo
  // toolbar buttons + Ctrl/Cmd+Z / Ctrl+Y apply snapshots through applyLayoutState
  // (PedigreeChart), which invalidates any in-flight worker layout first so a queued
  // settle can't clobber the restore. Structural edits (add/remove person) are transparent
  // to the stack: undoing past an add keeps the person — the engine auto-positions
  // unranked nodes and ignores stale uids in old snapshots.
  ENABLE_UNDO_REDO: true,

  // SAVED VIEWS (Phase 2 of PLAN-pedigree-undo-redo-and-saved-views, v1.1).
  // Named "virtual copies" of the layout: an alternate arrangement (pins /
  // order / lock) + a render-time hidden overlay of the SAME people, persisted
  // in the `pedigreeviews` collection (never the standard pedigreeconfigs doc).
  // The live layout has an ACTIVE SOURCE — Standard, or a view — and the
  // layout autosaves route to it. Private (user-scope) views are live virtual
  // copies; group-shared views apply READ-ONLY (their edits stay session-only
  // until an explicit "Update view" — plan decision 8). Selector lives in the
  // record header (decision 6).
  ENABLE_PEDIGREE_SAVED_VIEWS: true,

  // REPRODUCTIVE NODES (Track A of claude-MDs/PLAN-pedigree-reproductive-nodes.md;
  // build-ahead behind this flag while the proband-switching data-model remodel is
  // pending). Pregnancies (diamond/□/○ + "P"; loss triangles for SAB/ECT/TOP/stillbirth),
  // gamete donors (□/○ + "D") and surrogates (○ + "S") as pedigree nodes per Bennett 2022
  // (Figs 4/5). Design: a pregnancy is an ordinary YOUNGEST-CHILD node (reuses isFetus); a
  // donor/surrogate is a SATELLITE placed beside the carrier with a solid `repro-descent`
  // edge and NO mating line — kept OUT of the contour engine (buildNuclearFamilies/
  // buildJunctionGraph) so existing families never enter the new path. Every consumer is
  // gated on this flag AND on a node carrying `reproKind` (see REPRO_KIND / isReproNode
  // below). Default OFF: with no repro data present the engine + render are byte-identical
  // to today (proven by the existing corpus staying green with this scaffold in place).
  ENABLE_REPRO_NODES: false,

  // Dual-parent adoption (Bennett Fig 3): an adopted-in individual keeps their ADOPTIVE
  // parents as the structural descent (dashed line, unchanged) AND gains a SOLID reference
  // connector to their BIOLOGICAL parents (bioFatherId/bioMotherId) elsewhere in the graph —
  // the adopted-by-relative case. Default OFF: with no bio-parent links set, render + layout
  // are byte-identical to today. See claude-MDs/PLAN-pedigree-adoption-dual-parent.md.
  ENABLE_ADOPTION_DUAL_PARENT: false,

  // RUNAWAY GUARD (P1.4): the crossing-minimising reorder search (best-of-both +
  // bidirectional) is super-linear in family size and blows up on a very large /
  // deeply-interconnected pedigree (the largest real corpus family is 278 people / ~12s).
  // Above this many people the engine SKIPS the search and falls back to the single
  // descent-forest apportionment (no search, no refine) — fast and overlap-free, but not
  // crossing-optimised; the result is flagged `simplifiedLayout` so the UI can prompt
  // "drag / add a proxy to refine". Set well ABOVE any real family so quality is never
  // degraded for them; this is purely a safety net against an unbounded background compute.
  MAX_LAYOUT_SEARCH_SIZE: 500,

  // LOAD-PERF B1 — REDUNDANCY ELISION (byte-identical; default ON, kill switch). Removes two
  // pure redundancies the profiler found (see claude-MDs/PLAN-pedigree-load-performance.md):
  // (B1a) a de-straddle candidate's fixpoint `free` rung recomputes the incumbent layout it
  // was derived from — reuse the incumbent instead; (B1b) best-of-both runs the bidirectional
  // pipeline even when the family is `bidirUnsupported`, where it falls back to the identical
  // descent-forest apportionment — reuse the descent-forest run. Both produce byte-identical
  // output; the flag lets a parity test compare on/off and provides a one-flip rollback.
  ENABLE_LAYOUT_REDUNDANCY_ELISION: true,

  // LOAD-PERF B2 — PARALLEL LAYOUT (default OFF; browser-acceptance gated). The bidirectional
  // contour runs its crossing-minimising search TWICE per layout (ancestry modes 'nudge' then
  // 'side'), sequentially — the dominant cost (~6s + ~10s of a ~19s 214-person layout). When on,
  // useAsyncLayout runs the two modes (and the descent-forest, for a both-ancestried family) as
  // CONCURRENT tasks on a Web Worker pool via pedigreeParallel(), for a ~2x wall-clock cut with
  // BYTE-IDENTICAL output (loadPerfParallelParity.test.js pins parallel == sync across the
  // corpus). OFF = the single-worker path (unchanged). Real-worker wall-clock is browser-only.
  // NOW ALSO COVERS THE LOCKED REOPEN (a saved pedigree's establishedOrder fixpoint): the
  // free/seeded/enforced rungs run speculatively in parallel, each decomposed into its
  // nudge/side/descent-forest tasks — wall-clock ≈ one parallel best-of-both instead of up
  // to three sequential ones. ON for in-app acceptance testing (Tim, 2026-07-19); the parity
  // suite (loadPerfParallelParity) pins parallel == sync including the locked rungs + beacon.
  ENABLE_PARALLEL_LAYOUT: true,
  // Upper bound on the layout worker pool; the runtime uses min(this, hardwareConcurrency - 2).
  PARALLEL_LAYOUT_MAX_WORKERS: 4,

  // LOAD-PERF B3a — SCORING RENDER MODEL (byte-identical; default ON, kill switch). The
  // crossing-scoring hot path (crossingRefine + the reorder search) builds a render model
  // hundreds of times purely to count crossings, then reads only node x/y + edge structure —
  // never the SVG `d` path strings or the spread clinical `person.data`. When ON, those scoring
  // builds skip that work (the dominant per-call cost). The FINAL rendered model is always
  // built full, so output is unchanged; the flag lets a parity test compare on/off.
  ENABLE_SCORING_RENDER_MODEL: true,

  // Contour layout only: a decoupled SECONDARY containment bridge (a doubly cross-linked
  // family's cross-cousin marriage — G-1-01373's 25-35) is drawn as a STRETCHED marriage
  // bar across the chart. To keep it from running straight over the row of nodes between
  // the partners, the bar is routed on a lane this fraction of LEVEL_HEIGHT BELOW the
  // couple's row (with the union node on the lane, so the shared child drops from the bar).
  // Below the row — not above — because the row below is near-empty for this class (few
  // gen-2 nodes have children), so the lane sweeps almost no drops.
  STRETCH_MARRIAGE_LANE: 0.35,

  ENABLE_LINE_INTERSECTION_HOOKS: true,
  LINE_HOOK_SIZE: 80,
  LINE_HOOK_RADIUS: 70,
  EXTRA_SPOUSE_CHILD_X_STEP: 5 * 100,

  // ASYM-junction minimum clearance from each partner, in SYMBOL_SIZE units
  // (SS01 / G-1-01430, 2026-07-12 — Tim's rule 2, verbatim intent: a union's
  // drop "must NEVER descend from directly under ONE parent: clinically that
  // reads as children of a single parent"). The full-bar [0,1] clamp let an
  // out-of-reach children's midpoint park the junction EXACTLY on a partner's
  // column (t=0/1 — the 01430 locked add: u(2,3) dead under 3). Clamp to an
  // interior band instead: at least this × SYMBOL_SIZE px from each partner
  // (0.75 = half a symbol + a quarter-symbol margin, so the drop clears the
  // symbol silhouette), degrading to the bar midpoint when the bar is too
  // short to hold the band. Shared by the render (layout.js) and the metric
  // (crossingRefine.js unionVX) so they never disagree (the G-1-01406 lesson).
  ASYM_JUNCTION_PARENT_CLEARANCE: 0.75,

  TWIN_SPACING_MULTIPLIER: 1, // Reduce spacing to 40% of normal for twins
  TWIN_DIAGONAL_MEETING_HEIGHT: 0.5, // Meeting point at 50% height between union and twins
  TWIN_BAR_WIDTH_MULTIPLIER: 0.5, // Horizontal bar spans 80% of twin group width

  // === Node Types ===
  NODE_TYPE: {
    NODE: 'NODE',
    UNION: 'UNION'
  },

  DEBUG_MODE: false,
  LOG_POSITIONING: false
};

export function applyCanvasPreferences(prefs) {
  if (!prefs) return;
  if (prefs.symbol_size) config.SYMBOL_SIZE = prefs.symbol_size;
  if (prefs.horizontal_spacing) {
    const h = (prefs.horizontal_spacing / 100) * BASE_NODE_GAP;
    config.NODE_GAP = h;
  }
  if (prefs.vertical_spacing) {
    config.LEVEL_HEIGHT = (prefs.vertical_spacing / 100) * BASE_LEVEL_HEIGHT;
  }
}

export const GENDER = Object.freeze({
  male: 'male',
  female: 'female',
  unknown: 'unknown'
});

export const PARENT_ROLE = Object.freeze({
  father: 'father',
  mother: 'mother',
  null: null
});

export const SIBLING_TYPE = Object.freeze({
  normal: 'normal',
  twin: 'twin',
  triplet: 'triplet',
  quadruplet: 'quadruplet'
});

export const TWIN_TYPE = Object.freeze({
  singleton: 'singleton',
  monozygotic: 'monozygotic',
  dizygotic: 'dizygotic',
  unknown: 'unknown'
});

// === Reproductive nodes — Track A node-field contract (PLAN §5.1) ===
// The frozen vocabulary the engine/render/fixtures read off a normalized node. This is
// the SEAM between Track A (engine reads these) and the deferred Track B (backend
// populates them). A node's `data.reproKind` is the discriminator; absent/null => an
// ordinary person (every existing chart), so existing layout is untouched.
export const REPRO_KIND = Object.freeze({
  pregnancy: 'pregnancy',
  gameteDonor: 'gameteDonor',
  surrogate: 'surrogate'
});

// Pregnancy outcome subtype (data.pregnancy.outcome). Bennett Fig 4: an ongoing
// pregnancy plus the loss states SAB / TOP / ectopic / stillbirth; `liveBirth` lets a
// delivered pregnancy convert to an ordinary child without discarding the record.
export const PREGNANCY_OUTCOME = Object.freeze({
  ongoing: 'ongoing',
  sab: 'sab',
  top: 'top',
  ectopic: 'ectopic',
  stillbirth: 'stillbirth',
  liveBirth: 'liveBirth'
});

// Gamete-donor type (data.donorType) — Bennett Fig 5 "D". `embryo` = a donated embryo
// (both gametes donated, e.g. Fig 5 scenario 1f).
export const DONOR_TYPE = Object.freeze({
  sperm: 'sperm',
  ovum: 'ovum',
  embryo: 'embryo'
});

// Pure, model-independent predicates over a normalized node. `n` may be a PedigreePerson
// instance (once Track A hoists the fields) or a raw data / render-node object (fixtures),
// so both `n.reproKind` and `n.data.reproKind` are honoured. Inert until ENABLE_REPRO_NODES
// gates their consumers on.
export function reproKindOf(n) {
  if (!n) return null;
  if (n.reproKind != null) return n.reproKind;
  if (n.data && n.data.reproKind != null) return n.data.reproKind;
  return null;
}

export function isReproNode(n) {
  return reproKindOf(n) != null;
}

export function isPregnancyNode(n) {
  return reproKindOf(n) === REPRO_KIND.pregnancy;
}

export function isGameteDonorNode(n) {
  return reproKindOf(n) === REPRO_KIND.gameteDonor;
}

export function isSurrogateNode(n) {
  return reproKindOf(n) === REPRO_KIND.surrogate;
}

// Whether a person can be the gestational carrier of a pregnancy. Requires ASSIGNED
// FEMALE AT BIRTH — `gender` here is the assigned sex at birth (genderMappings maps a
// trans man "Male - AFAB" → 'female', a trans woman → 'male'), so this admits cis women
// AND Bennett's trans-man / nonbinary-AFAB carriers (Fig 5.1d/e). It deliberately EXCLUDES
// unknown/indeterminate/UAAB: a pregnancy presupposes a defined gestational carrier (a
// uterus), so the sex assigned at birth must be established as female first — safer than
// asserting a biologically-unconfirmed pregnancy. Bennett Fig 5 has no unknown-sex carrier.
export function canCarryPregnancy(n) {
  const gender = (n && (n.gender ?? (n.data && n.data.gender))) || null;
  return gender === GENDER.female;
}

// A satellite is a repro node kept OUT of the contour engine (donor or surrogate),
// as opposed to a pregnancy, which is placed as an ordinary child.
export function isReproSatelliteNode(n) {
  const k = reproKindOf(n);
  return k === REPRO_KIND.gameteDonor || k === REPRO_KIND.surrogate;
}

// === Reproductive-node RENDER helpers (Track A / A2-A3 render) =============
// Real render-sizing keys (the SYMBOL_WIDTH / REDUCE_*_FOR_FETUS keys the dead
// Person.js getters reference never existed → NaN). Linear multiples of
// config.SYMBOL_SIZE; squared at d3 .size() (which takes px² area). Attached to
// `config` so a future spacing pref can scale them alongside SYMBOL_SIZE.
config.FETUS_SYMBOL_SCALE = 0.6; // ongoing-pregnancy symbol (smaller than a person)
config.REPRO_LOSS_SCALE = 0.5; // small loss triangle

// Loss states drawn as a small triangle (Bennett Fig 4). The render uses
// d3.symbolTriangle (apex-up, the shape the plan says to reuse); final apex
// orientation is a browser-QA item against Bennett Fig 4. Stillbirth is
// deliberately NOT here: per Bennett §2.1 a stillbirth is a PERSON symbol
// carrying an "SB" label, not a loss triangle.
export const PREGNANCY_LOSS_LABEL = Object.freeze({
  sab: 'SAB',
  top: 'TOP',
  ectopic: 'ECT'
});
const PREGNANCY_LOSS_OUTCOMES = new Set(Object.keys(PREGNANCY_LOSS_LABEL));

export function pregnancyOutcomeOf(n) {
  const p = (n && (n.pregnancy || (n.data && n.data.pregnancy))) || null;
  return p ? p.outcome : null;
}

// A pregnancy loss drawn as a triangle: SAB / TOP / ectopic (NOT stillbirth).
export function isPregnancyLoss(n) {
  return (
    isPregnancyNode(n) && PREGNANCY_LOSS_OUTCOMES.has(pregnancyOutcomeOf(n))
  );
}
export function pregnancyLossLabelOf(n) {
  return PREGNANCY_LOSS_LABEL[pregnancyOutcomeOf(n)] || '';
}

// What actually prints beneath a loss triangle on the chart (Bennett Fig 4). Only an
// ECTOPIC carries a text abbreviation ("ECT"); a spontaneous abortion (SAB) is a bare
// triangle, and a termination (TOP) is distinguished by a diagonal strike through the
// triangle (see isTerminationOfPregnancy + the slash in Canvas.jsx), not by text.
// PREGNANCY_LOSS_LABEL keeps the full human names for menus/tooltips/editor.
export function pregnancyLossRenderLabel(n) {
  return isPregnancyNode(n) &&
    pregnancyOutcomeOf(n) === PREGNANCY_OUTCOME.ectopic
    ? PREGNANCY_LOSS_LABEL.ectopic
    : '';
}

// Termination of pregnancy (TOP): Bennett marks it with a diagonal line struck
// through the loss triangle — the same convention (and slash) used for a deceased
// individual, scaled to the smaller triangle.
export function isTerminationOfPregnancy(n) {
  return isPregnancyNode(n) && pregnancyOutcomeOf(n) === PREGNANCY_OUTCOME.top;
}

// Stillbirth: a PERSON symbol carrying an "SB" label (Bennett §2.1).
export function isStillbirth(n) {
  return (
    isPregnancyNode(n) && pregnancyOutcomeOf(n) === PREGNANCY_OUTCOME.stillbirth
  );
}

// An ongoing pregnancy — the only outcome that draws the diamond/gender + "P".
export function isOngoingPregnancy(n) {
  return (
    isPregnancyNode(n) && pregnancyOutcomeOf(n) === PREGNANCY_OUTCOME.ongoing
  );
}

// SINGLE SOURCE OF TRUTH for a node's rendered symbol size (d3 .size() takes px²
// area). Pure (no d3), so both the engine (path.js/layout.js line-of-descent geometry)
// and the render (Canvas.jsx symbol + clip twin, via the reproSymbol re-export) share
// it and can never drift. UNION → 0; with the flag on, a loss triangle and an ongoing
// pregnancy are smaller than a person; every other node — and everything when the flag
// is off — is the unchanged full SYMBOL_SIZE², so the golden corpus stays byte-identical.
export function nodeSymbolArea(d) {
  if (d.kind === config.NODE_TYPE.UNION) return config.SYMBOL_SIZE * 0;
  if (config.ENABLE_REPRO_NODES) {
    if (isPregnancyLoss(d)) {
      const s = config.SYMBOL_SIZE * config.REPRO_LOSS_SCALE;
      return s * s; // small loss triangle (SAB/TOP/ECT)
    }
    if (isOngoingPregnancy(d)) {
      const s = config.SYMBOL_SIZE * config.FETUS_SYMBOL_SCALE;
      return s * s; // ongoing pregnancy — smaller than a person symbol
    }
  }
  return config.SYMBOL_SIZE * config.SYMBOL_SIZE; // ← unchanged non-repro path
}

// Half the symbol's vertical extent, derived from nodeSymbolArea (single source), used
// to attach a line of descent to a symbol's top/bottom edge (e.g. the diagonal gamete-
// donor line, reproDescentPath). sqrt(area)/2 is the exact half-side for a square and a
// close approximation for the diamond/triangle a pregnancy uses.
export function nodeSymbolHalfHeight(d) {
  return Math.sqrt(nodeSymbolArea(d)) / 2;
}

// Role label shown instead of a name for an anonymised donor/surrogate.
export function reproRoleLabelOf(n) {
  const k = reproKindOf(n);
  if (k === REPRO_KIND.gameteDonor) {
    const dt = n.donorType || (n.data && n.data.donorType);
    if (dt === DONOR_TYPE.sperm) return 'Sperm donor';
    if (dt === DONOR_TYPE.ovum) return 'Egg donor';
    if (dt === DONOR_TYPE.embryo) return 'Embryo donor';
    return 'Donor';
  }
  if (k === REPRO_KIND.surrogate) return 'Surrogate';
  return '';
}

// Bennett symbol shape for a donor/surrogate is driven by donorType/role, NOT
// gender: sperm donor → square, ovum donor & surrogate → circle. Returns a
// GENDER value the shape switch understands, or null to fall back to gender.
export function reproSatelliteGenderShape(n) {
  const k = reproKindOf(n);
  if (k === REPRO_KIND.surrogate) return GENDER.female; // circle
  if (k === REPRO_KIND.gameteDonor) {
    const dt = n.donorType || (n.data && n.data.donorType);
    if (dt === DONOR_TYPE.sperm) return GENDER.male; // square
    if (dt === DONOR_TYPE.ovum) return GENDER.female; // circle
  }
  return null; // embryo donor / unset → caller falls back to gender
}
